import { ICurrentConversation } from '../components/modalTemplates/types';
import { ILead } from 'src/interfaces/lead';
import { getTemplateArea } from 'src/components/Lead/WhatsApp/utils/getTemplateArea';
import {
    OUTCOME_DIRECTION,
    WHATSAPP_CHANNEL,
} from 'src/constants/conversations';
import { IConversation } from 'src/interfaces/conversation';
import { DEFAULT_CONVERSATION_CONFIG } from 'src/modules/conversations/constants/templates';
import { TChannel, TDirection } from 'src/interfaces/conversation';
import { TAreas } from 'src/constants/lead/leadTypes';
import { IStore } from 'src/interfaces/store';
import { IVehicle } from 'src/interfaces/vehicle';

/**
 * Base conversation configuration
 * Contains common fields shared between Lead and Conversation
 */
interface IBaseConversationConfig {
    _id: string;
    name: string;
    vehicle?: IVehicle;
    store: Partial<IStore>;
    channel: TChannel;
    direction: TDirection;
    to: string;
    from: string;
    area: TAreas;
    phone: string;
    isContentTemplateBuilder: boolean;
}

/**
 * Factory to create conversations from different sources
 * Uses a base configuration and extends it for specific contexts
 * @param {ILead} ILead
 * @param {IConversation} IConversation
 * @returns {ICurrentConversation} ICurrentConversation
 * @example
 * const conversation = ConversationFactory.createFromLead(lead);
 * const conversation = ConversationFactory.createFromConversation(conversation);
 */
export class ConversationFactory {
    static createBaseConfig(
        id: string,
        name: string,
        phone: string,
        area: TAreas,
        store: Partial<IStore>
    ): IBaseConversationConfig {
        return {
            _id: id,
            name,
            store,
            channel: WHATSAPP_CHANNEL,
            direction: OUTCOME_DIRECTION,
            to: phone,
            from: '',
            area,
            phone,
            isContentTemplateBuilder: true,
        };
    }

    /**
     * Creates conversation from Lead
     * Adds lead-specific fields
     */
    static createFromLead(lead: ILead): ICurrentConversation | null {
        const area = getTemplateArea(lead);

        if (typeof lead.store === 'string') return null;

        const baseConfig = this.createBaseConfig(
            lead._id,
            lead.name,
            lead.phone,
            area,
            lead.store
        );

        return {
            ...baseConfig,
            vehicle: lead?.vehicle,
            version: lead?.version,
            from: lead.from || '',
            lead: lead,
            channel: WHATSAPP_CHANNEL,
        };
    }

    /**
     * Creates conversation from existing conversation
     * Preserves conversation-specific fields
     */
    static createFromConversation(
        conversation: IConversation | ICurrentConversation
    ): ICurrentConversation {
        return {
            ...conversation,
            isContentTemplateBuilder:
                DEFAULT_CONVERSATION_CONFIG.isContentTemplateBuilder,
        };
    }
}
